import { isManifestPagePath } from "@/lib/navigation/manifest-hrefs";

/**
 * Pick out the in-app links in an assistant reply that deserve an action chip.
 *
 * Two kinds of href qualify. Dynamic per-item content (assignments, tests,
 * learning paths) carries ids that cannot be enumerated, so those are
 * allowlisted by prefix. Static app pages (reports, playbook, admin, …) are
 * matched against the navigation manifest instead — the same single source of
 * truth behind the header menus and the `get_app_pages` directory Sabine links
 * from — so a page she can hand out is a page that gets a chip, and an invented
 * URL never becomes one.
 *
 * This started as the home hero's own helper (`lib/home-sabine/`). It moved
 * here when `AIChat` began offering the same chips by default, so the result
 * coach, the Deal Clinic coach and the content builder answer navigation the
 * way the home page does rather than each growing its own version.
 *
 * Client-safe: no server imports, and the only `window` use is guarded.
 */

export const ACTION_LINK_LIMIT = 5;

export const ACTION_HREF_PREFIXES = [
  "/dashboard/role-plays/assignments/",
  "/tests/assignments/",
  "/faq-tests/assignments/",
  "/glossary-tests/assignments/",
  "/dashboard/learning-paths/",
  "/learning-paths/",
  // A practice run carries its scenario in the query string
  // ("/simulator/pitch?id=57&mode=regular"), so the manifest — which knows
  // "/simulator" and nothing below it — cannot match one. This is what the
  // result coach links when it recommends what to practice next.
  "/simulator/",
];

export function normalizeActionHref(rawHref) {
  const value = String(rawHref || "").trim();
  if (!value) return null;

  let href = value;
  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value);
      const currentOrigin =
        typeof window !== "undefined" ? window.location.origin : null;
      if (parsed.origin !== currentOrigin && parsed.hostname !== "app.salesably.ai") {
        return null;
      }
      href = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  } else if (value.startsWith("/") && !value.startsWith("//")) {
    // The browser resolves dot segments — literal or percent-encoded — when it
    // navigates, so the allowlist must judge that canonical pathname, not the
    // raw string ("/tests/assignments/../x" is really "/x").
    try {
      const parsed = new URL(value, "https://app.salesably.ai");
      href = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return null;
    }
  } else {
    // Everything else (protocol-relative, mailto:, bare-relative, anchors)
    // already failed both allowlists before; keep rejecting it explicitly.
    return null;
  }

  const allowed =
    ACTION_HREF_PREFIXES.some((prefix) => href.startsWith(prefix)) ||
    isManifestPagePath(href);
  return allowed ? href : null;
}

export function extractActionLinks(markdown = "") {
  const text = String(markdown || "");
  const links = [];
  const seen = new Set();
  const markdownLinkPattern = /\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
  let match;

  while ((match = markdownLinkPattern.exec(text))) {
    const label = match[1]?.replace(/[*_`]/g, "").trim();
    const href = normalizeActionHref(match[2]);
    if (!label || !href || seen.has(href)) continue;
    seen.add(href);
    links.push({ href, label });
    if (links.length >= ACTION_LINK_LIMIT) break;
  }

  return links;
}
